import type JQuery from 'jquery';

export {afterNextRender, rafDecorator} from '@exadel/esl/modules/esl-utils/async/raf';

/** Unwrap JQuery object */
export const unwrap = <T extends HTMLElement = HTMLElement>(el: JQuery<T> | T): T => {
  if (el && 'length' in el) return (el as JQuery)[0] as T;
  return el as T;
};

/** Dispatch native browser Custom Event */
export const dispatchNativeEvent = (
  el: JQuery | HTMLElement,
  name: string,
  options: CustomEventInit = {bubbles: true}
) => {
  const event = new CustomEvent(name, options);
  const _el = unwrap(el);
  _el && _el.dispatchEvent(event);
};

/** Stub function to stop event bubbling */
export const stopEventBubbling = (e: Event | JQuery.Event) => e.stopPropagation();
stopEventBubbling.dontChangeContext = true;

/** Clear inner HTML IE safe */
export function clear($el: HTMLElement | JQuery<HTMLElement>) {
  const el = unwrap($el);
  if (!el) return;
  while (el.firstChild) el.removeChild(el.firstChild);
}

/** Render content to $el using render function and data */
export function rerender<T>($el: HTMLElement | JQuery<HTMLElement>, renderer: (data: T) => JSX.Element | HTMLElement | string, data?: T) {
  const el = unwrap($el);
  if (!el) return;
  clear(el);
  const result = renderer(data);
  if (typeof result === 'string') {
    el.innerHTML = result;
  } else {
    el.appendChild(result);
  }
}

/** Render content to $el using render function and collection of data items */
export function rerenderCollection<T>($el: HTMLElement | JQuery<HTMLElement>, renderer: (data: T, index: number) => JSX.Element | HTMLElement | string, data: T[]) {
  const el = unwrap($el);
  if (!el) return;
  clear(el);
  (data || [])
    .map(renderer)
    .map((item: HTMLElement | string) => (typeof item === 'string') ? document.createTextNode(item) : item)
    .forEach((item) => item && el.appendChild(item));
}
